import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import {
  BookOpen, ChevronDown, ChevronLeft, ChevronRight, LogIn, User, Users, PhoneCall, HeartHandshake,
  CalendarCheck, GraduationCap, HandCoins, Send, ShieldCheck, KeyRound, Flame, Printer, Rocket, Check, ScrollText, MailPlus, Search, X, QrCode, MapPin, Lock, BellRing, Settings2,
} from "lucide-react";

function quickStartFor(user) {
  const role = user?.role;
  const caps = user?.caps || {};
  if (role === "member") {
    return [
      { icon: LogIn, title: "You're in!", text: "Set your own password on first sign-in, then you're ready to explore your portal." },
      { icon: User, title: "Complete your profile", text: "Add a photo and check your contact details on My Portal." },
      { icon: GraduationCap, title: "Start a course", text: "Open My Courses, work through lessons, and earn a certificate at 100%." },
      { icon: HeartHandshake, title: "Stay connected", text: "Read church messages, reply, and send a prayer request anytime." },
      { icon: MapPin, title: "Join a cell group", text: "Open My Cell Group to send your cell group response, then see your group, host and updates once you're placed." },
    ];
  }
  const steps = [
    { icon: LogIn, title: "Welcome, team!", text: "This is your church management hub. The left menu adapts to what your role can access." },
  ];
  if (role === "admin" || caps.manage_members)
    steps.push({ icon: Users, title: "Add your members", text: "Go to Members → Add Member. A login + welcome message is created automatically for anyone with an email." });
  if (role === "admin" || caps.pastoral_care)
    steps.push({ icon: PhoneCall, title: "Care for new people", text: "New Members & Follow-Up flags anyone not contacted in 7+ days — log every call and text." });
  steps.push({ icon: Send, title: "Reach everyone", text: "Use Messaging to send portal, email or SMS announcements to a person, a team, or the whole church." });
  if (role === "admin" || caps.is_admin)
    steps.push({ icon: ShieldCheck, title: "Set up access", text: "Roles & Permissions decides what each role and team can see and do. Users & Access lets you adjust one person." });
  return steps;
}

function QuickStart({ user }) {
  const steps = quickStartFor(user);
  const [i, setI] = useState(0);
  const step = steps[i];
  const Icon = step.icon;
  const isLast = i === steps.length - 1;
  return (
    <div data-testid="guide-quickstart" className="no-print rounded-2xl overflow-hidden mb-6 text-white shadow-sm" style={{ background: "linear-gradient(135deg, var(--sj-secondary), var(--sj-primary))" }}>
      <div className="p-6 sm:p-8">
        <div className="flex items-center gap-2 text-white/80 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
          <Rocket className="h-4 w-4" /> 60-second quick start
        </div>
        <div className="flex items-start gap-4 min-h-[92px]">
          <div className="h-12 w-12 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center shrink-0">
            <Icon className="h-6 w-6" />
          </div>
          <div>
            <h3 className="font-head text-xl font-extrabold">{step.title}</h3>
            <p className="mt-1 text-white/90 text-sm leading-relaxed max-w-lg">{step.text}</p>
          </div>
        </div>
        <div className="mt-6 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            {steps.map((_, idx) => (
              <button key={idx} onClick={() => setI(idx)} data-testid={`quickstart-dot-${idx}`} className="h-2 rounded-full transition-all" style={{ width: idx === i ? 22 : 8, background: idx === i ? "#fff" : "rgba(255,255,255,0.4)" }} aria-label={`Step ${idx + 1}`} />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="quickstart-back" disabled={i === 0} onClick={() => setI(i - 1)} className="flex items-center gap-1 rounded-full px-3 py-2 text-sm font-semibold bg-white/15 hover:bg-white/25 transition-colors disabled:opacity-40">
              <ChevronLeft className="h-4 w-4" /> Back
            </button>
            <button data-testid="quickstart-next" onClick={() => setI(isLast ? 0 : i + 1)} className="flex items-center gap-1 rounded-full px-4 py-2 text-sm font-bold bg-white transition-colors hover:brightness-95" style={{ color: "var(--sj-secondary)" }}>
              {isLast ? <>Start again</> : <>Next <ChevronRight className="h-4 w-4" /></>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const SECTIONS = [
  {
    id: "getting-started",
    title: "Getting started & signing in",
    icon: LogIn,
    show: () => true,
    steps: [
      "Open the portal and enter the email and password you were given.",
      "First time in? You'll be asked to set your own password before you can continue.",
      "Forgot your password? Click \"Forgot password?\" on the sign-in page, enter your email, and follow the reset link we send you.",
      "Use the green menu on the left to move around — your own pages (My Portal, My Courses, My Cell Group) are at the top. On a phone, tap the menu icon at the top-left to open it. Type in \"Search menu…\" to jump straight to any page.",
      "Not a member yet but want to join a cell group? Tap \"Complete the response form\" under the sign-in box — no account needed.",
      "Need help? Look at the footer at the bottom of any page for the \"Members Portal Support\" contact — call or email the number and address shown there.",
    ],
  },
  {
    id: "my-portal",
    title: "Your member portal",
    icon: User,
    show: (u) => u?.role === "member",
    steps: [
      "My Portal shows your profile, attendance summary and messages from the church.",
      "Click \"Edit profile\" to update your own name, email, phone number and date joined. (Your membership type can only be changed by a church leader.)",
      "Upload a profile photo and keep your contact details up to date.",
      "Read messages from church teams and reply right inside the portal.",
      "Submit a prayer request — the pastoral team will see it and pray with you.",
      "Look out for the seasonal welcome message or verse on your portal banner — the church team updates it through the year.",
    ],
  },
  {
    id: "my-cell-group",
    title: "My Cell Group",
    icon: MapPin,
    show: () => true,
    steps: [
      "Open \"My Cell Group\" from your menu. If you're not in a group yet, tap \"Complete the response form\" in the green banner and add your postcode, when you can meet, and whether you're open to hosting. Tap \"Share form with a friend\" to copy the public form link.",
      "Once you've sent it you'll see \"You're on the list\" — you can update your response any time until you're placed.",
      "When your group is approved you'll see the group name, your host, the hosting address (if shared) and the meeting day, plus the names of everyone in your group. Phone numbers, emails and members' addresses are never shown.",
      "Group updates from your host appear at the top of the page, and you also get a portal message when a new one is posted.",
      "Hosts: you'll see a \"Group updates\" box — write a short note (up to 500 characters) and tap \"Post update\" to tell your group about meeting changes. You can remove your updates with the bin icon.",
      "Filled in the public form before you had an account? It's linked to your account automatically by your email or phone number.",
    ],
  },
  {
    id: "my-courses",
    title: "Courses, assignments & feedback",
    icon: GraduationCap,
    show: (u) => u?.role === "member",
    steps: [
      "Open \"My Courses\" to see the discipleship courses you're enrolled in, and pick your cohort/class if asked.",
      "Work through each lesson and mark it complete as you go. Some lessons have a live-class link and a start time — tap it to join, and you're marked present automatically.",
      "If a lesson has an assignment, write your answer and/or upload a file (PDF, photo, Word, etc.). Required assignments must be submitted before you can complete the lesson.",
      "Your tutor reviews your work and can leave feedback right on your file — coloured highlights and pinned comments on a PDF, plus written notes. Open your submission to read it all in the built-in viewer (no download needed).",
      "Reply to any comment to ask a question, tick \"Mark as done\" once you've actioned a note, or give a quick 👍 to show you've read it.",
      "Join your cohort's class discussion to chat with classmates, your class rep and your tutor.",
      "When you reach 100% and your tutor approves your graduation, download your personalised completion certificate as a PDF from your portal.",
    ],
  },
  {
    id: "shared-qr",
    title: "Church QR codes",
    icon: QrCode,
    show: (u) => u?.role === "member",
    steps: [
      "Open \"Church QR codes\" from your menu to see handy QR codes the church team has shared with you — for events, giving, sign-up forms, flyers and more.",
      "Each code has a short description so you know what it's for, and codes are grouped into categories (Events, Giving, Forms…) — tap a category tab to quickly find the right one.",
      "Tap \"Open link\" to go straight to the page it points to, or tap \"View flyer\" to read a shared flyer full-screen right inside the app.",
      "Tap \"Share\" to send a code straight to WhatsApp or Messages from your phone, or \"Save image\" to download the QR and pass it on. You can also point your camera at the on-screen code.",
    ],
  },
  {
    id: "members",
    title: "Managing members",
    icon: Users,
    show: (u) => u?.role === "admin" || u?.caps?.manage_members,
    steps: [
      "Go to Members to view your church family directory. Filter by status or search by name, email or phone.",
      "Click any column heading (Name, Type, Status, Joined, Postcode, Last logged in) to sort the list — click again to reverse the order.",
      "Click \"Add Member\" to add someone. You can record their address and postcode too. If you include an email, a portal login is created and a welcome message (email + text) with their temporary password is sent automatically.",
      "The \"Last logged in\" column shows when each member last signed in — whether by email or mobile number — or \"Never\" if they haven't logged in since the feature went live.",
      "A small delivery badge (Delivered / Failed / Simulated) shows the status of the last message sent to each member, so you can spot bad phone numbers or email addresses.",
      "Use the pencil icon to edit details, and the key icon to set a new password — you can then email or text the login straight to them.",
      "Spot duplicates fast: click \"Review duplicates\" to see people who appear twice (same phone number or same name). Open a group and tap \"Review & merge\" to combine them.",
      "You can also merge from a member's ⋯ menu — choose \"Merge duplicate…\", pick the other record, decide which one to keep, and confirm. Everything (courses, attendance, prayers, teams, login) moves onto the kept record.",
      "Made a mistake? Right after a merge a green bar appears with an \"Undo merge\" button — you have 10 minutes to reverse it and the deleted record is fully restored.",
      "Deleting a member removes them completely — their login, team memberships, care history, prayer requests and attendance — after a confirmation that lists exactly what will be removed.",
      "Use \"Bulk Import\" to add many members at once from a CSV. The template includes a Prayer Request column and Pastoral Call 1/2/3 columns — use the format \"Date | Outcome | Note\" so past calls appear in the member's care history with the real dates.",
      "Use \"Export CSV\" to download the whole directory.",
      "Members in a cell group show a small green badge under their name (group · host). Open Edit Member to see their cell group, host and meeting day at the top of the form.",
    ],
  },
  {
    id: "follow-up",
    title: "New members, pastoral care & follow-up",
    icon: PhoneCall,
    show: (u) => u?.role === "admin" || u?.caps?.pastoral_care,
    steps: [
      "New Members & Follow-Up has two tabs: Pastoral (New Members) and Follow-Up. Every new member lands in Pastoral first so your team can make the initial calls.",
      "The list columns are Name, Last contacted, Joined, Caller and Actions. When assigning a caller, only members of that team are listed.",
      "Anyone not contacted in 7+ days is highlighted in red. Tap \"Overdue only\" to focus on just those people, or jump there from the Dashboard's Overdue tile.",
      "Log every call or text with \"Log call\"/\"Log text\". Pick who actually made the contact from the \"Made by\" list so attribution stays accurate — the Activity Log shows \"by {name}\".",
      "Each member card shows their date joined. Use the Sort dropdown to order the list by most overdue, name, or date joined.",
      "Tap \"History\" on any member for a full timeline of calls, texts, prayer requests and notes. Care history is view-only and cannot be exported or downloaded, to keep members' pastoral records confidential.",
      "Use \"Check in with all overdue\" to send a warm text/email to everyone overdue in one go.",
      "Members automatically move from Pastoral to Follow-Up after a set number of weeks, or once 2 pastoral calls are logged, or when you hand them off manually. When someone's settled, mark them Integrated.",
    ],
  },
  {
    id: "prayer",
    title: "Prayer requests",
    icon: HeartHandshake,
    show: (u) => u?.role === "admin" || u?.caps?.pastoral_care,
    steps: [
      "Prayer Requests lists everything members have submitted from their portal.",
      "Review each request and mark it as prayed for or actioned.",
    ],
  },
  {
    id: "attendance",
    title: "Attendance & conversions",
    icon: CalendarCheck,
    show: (u) => u?.role !== "member",
    steps: [
      "Use Attendance to record who attended each service or event.",
      "Conversions tracks new believers so the team can follow up and disciple them.",
    ],
  },
  {
    id: "giving",
    title: "Giving & donations",
    icon: HandCoins,
    show: (u) => u?.role === "admin" || !!u?.giving_open,
    steps: [
      "Members can give securely online from the Giving page once admins switch Giving on in Portal Settings.",
      "Staff with finance access can also record manual (cash/bank) gifts and review giving history.",
    ],
  },
  {
    id: "courses-staff",
    title: "Building & running courses",
    icon: GraduationCap,
    show: (u) => u?.role === "admin" || u?.caps?.manage_content,
    steps: [
      "Open Courses to create discipleship courses with lessons, media, video and live-meeting links. Add an assignment to any lesson (mark it required to block completion until it's submitted). Publish a course to make it available to members.",
      "Use cohorts (named intakes) to run the same course for several classes at once — each cohort can have its own coordinator, tutor and class rep. A cohort filter across the Register, Tracker and reports scopes everything to that class.",
      "Register tab: take attendance on a grid (tap a cell to cycle Present → Late → Absent → Excused), add/reorder/bulk-add sessions, import a class from CSV/XLSX, add learners, and star a learner to make them a Class Rep. Use the search box to jump to a learner by name.",
      "Live Attendance shows who joined via the live-class link versus who was marked manually. Learners who miss sessions are flagged \"at risk\" so you can send a nudge; repeated absences auto-flag a drop-off to Pastoral care.",
      "Submissions tab: review each learner's assignment in the built-in viewer — Word, Excel, PDF, images and text all preview in-app (PowerPoint shows a first-slide preview + download). Zoom and rotate scanned or photographed work.",
      "Give rich feedback on a PDF: drop pinned comments, or drag coloured highlights over a passage (green = Good, amber = Fix, red = Issue, blue = Note). Learners can reply, and you'll see when they mark a note done or give it a 👍. Record a mark and send written feedback with \"Review\". Use the search box to find a learner quickly, or \"Add on behalf\" to log a submission for someone who handed work in offline.",
      "Tracker tab: see every learner's assignment marks (M1–M5) and completion %, approve graduations, and issue certificates in bulk. Progress tab lets you mark completion, issue/revoke certificates and remove learners from the course. Both have a learner search box.",
      "HQ Report tab: export enrolment, attendance, completion, certificate and drop-off figures (including the Quarterly Discipleship report) as PDF or CSV.",
      "Certificates are never automatic — you approve graduation and issue the certificate before a learner can download it.",
    ],
  },
  {
    id: "church-qr",
    title: "Church QR codes",
    icon: QrCode,
    show: (u) => u?.role !== "member",
    steps: [
      "Church QR Codes (in the main menu) gives you ready-made codes for key links — Connect/Visitor sign-up, Online Giving, the Member Portal and \"Join a Cell Group\" — plus a generator to make your own for any link or text.",
      "QR Code Generator: give it a name, paste a link (event page, WhatsApp group, a flyer, YouTube…), add a short description that members will see, choose a category (Events, Giving, Forms…) to help members find it, then Save. Every saved code has its own tracking link, so scans are counted per poster.",
      "Print a branded poster or download the PNG for bulletins and screens. Edit a saved code's name, link, description or category any time and it updates everywhere.",
      "Share with members: tap \"Share with members\" on any saved QR to publish it to the members' \"Church QR codes\" page in their portal, where they can open it, view a linked flyer full-screen, or one-tap share it to WhatsApp/Messages. Tap again to unshare.",
      "QR Scan Stats: see how many visitors joined via the Connect QR (by month), the recent sign-ups, and a \"Scans by poster\" breakdown. A \"Shared with members — most opened\" leaderboard shows which shared codes members are tapping most.",
      "Connect Response: set an automatic thank-you (on-screen, plus optional SMS/email) shown after someone completes the Connect form. Create several named responses (e.g. Easter) and switch the active one — sign-up counts per response are shown on each chip.",
      "Add an optional information QR or flyer to the thank-you: upload flyers to your flyer library, give each a friendly name, and tap one to use it. You can also auto-send a follow-up SMS a day later, and an optional gentle second reminder a week later for anyone who hasn't visited yet. The \"Follow-up SMS log\" shows who has received them.",
    ],
  },
  {
    id: "messaging",
    title: "Messaging members",
    icon: Send,
    show: (u) => u?.role !== "member",
    steps: [
      "Use Messaging to send announcements by portal message, email or SMS — to an individual, a whole team, or the entire congregation.",
      "SMS is live and sends real texts from the church's Twilio number. Email is live via Resend.",
      "Every text automatically ends with \"This is an automated message; please do not reply. Log into your account to reply.\" so members don't reply to the number.",
      "Every message the system sends (welcomes, resets, check-ins, announcements, digests) is recorded — see the Message Log.",
    ],
  },
  {
    id: "message-log",
    title: "Message log & reports",
    icon: ScrollText,
    show: (u) => u?.role !== "member",
    steps: [
      "Message Log is a full audit trail of every message sent — with recipient, type, channel, status (Sent / Simulated / Failed) and who sent it.",
      "Filter by channel, status or a date range, and use \"Export CSV\" for offline records or monthly reporting.",
      "The bar chart shows messages sent per month by channel, so you can see engagement trends at a glance.",
    ],
  },
  {
    id: "message-templates",
    title: "Message templates (customise your wording)",
    icon: MailPlus,
    show: (u) => u?.role === "admin" || u?.caps?.is_pastor,
    steps: [
      "Message Templates lets you edit the exact wording of every automated message: the welcome message (email + SMS), the login-details message (email + SMS), the overdue pastoral check-in, and the weekly leaders' digest.",
      "You can also reword the course feedback notices learners receive in their portal — \"New assignment feedback\", \"Tutor replied to feedback\" and \"Assignment reviewed\" — using placeholders like {first_name}, {course_name}, {lesson_title}, {tutor_name} and {feedback}.",
      "Type your own text and drop in placeholders like {first_name}, {last_name}, {login_id}, {temp_password}, {portal_url} and {church_name} — just click a placeholder chip to insert it. They're filled in automatically with each member's real details when the message is sent.",
      "Click \"Preview\" to see how your message will look with sample details, then \"Save changes\" to make it live. A \"Customised\" tag shows which templates you've edited.",
      "Use \"Send test to me\" to receive the message in your own inbox (or phone, for SMS templates) before it goes out to members.",
      "Changed your mind? \"Reset to default\" instantly restores the original wording for that template.",
      "Only Admins and Pastors can edit templates.",
    ],
  },
  {
    id: "care-settings",
    title: "Care automations & settings",
    icon: PhoneCall,
    show: (u) => u?.role === "admin" || u?.caps?.is_admin,
    steps: [
      "In Users & Access → Pastoral care settings, choose how long new members stay with the Pastoral team (2, 3 or 4 weeks) before auto-handover to Follow-Up.",
      "Turn on \"Automatic weekly check-in\" to have the system text/email anyone overdue every week, using a message template you set.",
      "The \"Weekly overdue digest\" emails Pastoral & Follow-up leaders a summary of the care queue and who's overdue — use \"Send digest now\" to preview it any time.",
    ],
  },
  {
    id: "teams",
    title: "Teams & leaders",
    icon: Users,
    show: (u) => u?.role !== "member",
    steps: [
      "Teams group people for ministry. What each team can do (for example call members) is set in Roles & Permissions — being in a team no longer grants access by itself.",
      "Each person in a team is Head of Department, Assistant Head of Department or Team member — a badge beside their name shows which.",
      "Confidential teams: tap the lock toggle on a team card to hide it from members — they can't see it or apply. Use the \"Add member\" search on the card to place people in it directly.",
      "Create or rename a team at any time; the system prevents two teams having the same name.",
      "When adding members to a team, use the search box to find someone by name instead of scrolling the whole list.",
      "To set a team leader, open \"Assign leader\" and either choose an existing team member (no email needed — they lead with their current login) or set one up with an email address.",
    ],
  },
  {
    id: "admin",
    title: "Users, roles & access",
    icon: ShieldCheck,
    show: (u) => u?.role === "admin" || u?.caps?.is_admin,
    steps: [
      "Users & Access lets you assign roles and team memberships that control what each person can see and do.",
      "Access is permission-based: Admins and Pastors can do everything; everyone else gets exactly the abilities ticked for their role and team in Roles & Permissions — there are no hidden shortcuts.",
      "Need to give one person access without changing their role or team? Click the sliders icon in the Access column and tick the abilities for them directly (the list scrolls).",
      "Each person's effective role and permissions are shown on Users & Access so you can audit access at a glance.",
      "The \"Last logged in\" column shows when each staff member, leader or admin last signed in, or \"Never\".",
      "Click a column heading to sort the list, and use \"Recent access changes\" to see an audit of role, team and permission changes.",
      "The Danger Zone \"Reset all data\" wipes demo data — type RESET to confirm. Admin and standard teams are kept.",
    ],
  },
  {
    id: "roles-permissions",
    title: "Roles & Permissions",
    icon: Lock,
    show: (u) => u?.role === "admin" || u?.caps?.is_admin,
    steps: [
      "Systems Admin → Roles & Permissions lists every menu and sub-menu with a tick box per role (Admin, Pastor, Leader, Head of Department, Assistant Head, Team member, Member). Greyed-out ticks are system pages everyone always has.",
      "Choose \"By team\" to set permissions for one team (for example let Pastoral heads call members but not Finance). \"Copy team permissions\" copies one team's grid to another.",
      "Many items have a read-only option, so people can view a page without changing anything. Calendar, Gallery and Sermons each have their own view and manage permissions.",
      "Calling members needs both: the person's role must allow calling and their team must be allowed to call (off until you turn it on).",
      "Every change is recorded in the Permission Change Log with who changed what and when.",
    ],
  },
  {
    id: "calling-alerts",
    title: "Calling, alerts & portal settings",
    icon: BellRing,
    show: (u) => u?.role === "admin" || u?.caps?.is_admin,
    steps: [
      "Calls and texts to members show as \"Streams of Joy Erith London\". In Portal Settings choose the default calling app — \"Call through the app\" is the organisation default.",
      "Calling Audit shows who called which member and when. Filter by team and download it as a spreadsheet.",
      "Alerts lists every automatic message. Turn each one on or off, and use \"Send test\" to preview it by email or SMS to yourself or any number/email you type.",
      "Service reminders go out once a week listing the next 3 services, signed \"Streams of Joy Erith London\" with the app link. Set the day and time in Portal Settings.",
      "Seasonal banner message: in Portal Settings write a short welcome or verse (with an optional reference like \"Psalm 23:1\"), check the live preview and tap \"Save banner message\". It shows on every member's portal banner — untick \"Show on member portals\" to hide it.",
    ],
  },
  {
    id: "cell-group",
    title: "Cell Group planning",
    icon: Settings2,
    show: (u) => u?.role === "admin" || u?.caps?.cell_view,
    steps: [
      "Cell Group → Overview gives a dashboard of responses, postcode areas, hosting interest and follow-up readiness. \"Copy summary\" copies a briefing; \"Form QR poster\" prints an A4 poster with a QR code to the response form for services.",
      "Responses is the signup register (from the public form, portal and imports). Filter by postcode area, export a CSV, reveal contact details when needed, and see a green \"Member\" badge when a response is linked to the membership list (matched automatically by email or phone).",
      "Import: download Form Responses 1 from Google Sheets as CSV, choose the file and tap \"Preview import\". The preview shows new rows, updates, members matched, postcode problems and older duplicates (only the newest row per person is imported). Tap \"Import N rows\" to save.",
      "Postcode groups shows how many people live in each area — tap an area to see its responses.",
      "Group planning: confirm hosts and set their capacity and availability, then tap \"Generate draft groups\" (max 15 including the host; same postcode and same household kept together). On each card you can set missing availability, a Household ID (house icon), the meeting day, remove a member, and approve. Approved groups have a print icon for a printable group sheet, or use \"Print approved sheets\".",
      "Unassigned people appear at the bottom — tap \"Assign\" to place them into the nearest group with space.",
      "Data quality lists records needing review: fix postcodes inline, and for possible duplicates choose \"Merge into oldest\" (fills in missing details), \"Exclude record\" or \"Not a duplicate\". All decisions can be restored from the review history.",
      "Cell Audit records every import, plan, approval, merge and group update for accountability.",
    ],
  },
  {
    id: "passwords",
    title: "Passwords & security",
    icon: KeyRound,
    show: () => true,
    steps: [
      "Everyone sets their own password on first sign-in.",
      "Reset your own password anytime via \"Forgot password?\" on the sign-in screen.",
      "Admins and member managers can set a new temporary password for any member from the Members page.",
    ],
  },
];

const CATEGORIES = [
  { key: "start", label: "Getting started", ids: ["getting-started", "my-portal", "my-courses", "my-cell-group", "shared-qr", "passwords"] },
  { key: "courses", label: "Courses", ids: ["attendance", "courses-staff"] },
  { key: "people", label: "People", ids: ["members", "follow-up", "prayer", "giving", "teams"] },
  { key: "cell", label: "Cell Group", ids: ["cell-group"] },
  { key: "admin", label: "Admin", ids: ["messaging", "message-log", "message-templates", "care-settings", "church-qr", "admin", "roles-permissions", "calling-alerts"] },
];

function markMatches(text, q) {
  if (!q) return text;
  const lower = text.toLowerCase();
  const out = [];
  let start = 0, i = lower.indexOf(q, 0), key = 0;
  while (i !== -1) {
    if (i > start) out.push(text.slice(start, i));
    out.push(<mark key={key++} style={{ background: "#FCE8A6", color: "inherit", borderRadius: "3px", padding: "0 2px" }}>{text.slice(i, i + q.length)}</mark>);
    start = i + q.length;
    i = lower.indexOf(q, start);
  }
  if (start < text.length) out.push(text.slice(start));
  return out;
}

export default function UserGuide() {
  const { user } = useAuth();
  const [openId, setOpenId] = useState("getting-started");
  const [printAll, setPrintAll] = useState(false);
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const visible = SECTIONS.filter((s) => s.show(user));
  const sections = q
    ? visible.filter((s) => s.title.toLowerCase().includes(q) || s.steps.some((step) => step.toLowerCase().includes(q)))
    : visible;

  const handlePrint = () => {
    setPrintAll(true);
    setTimeout(() => {
      window.print();
      setPrintAll(false);
    }, 150);
  };

  const renderSection = (s) => {
    const isOpen = printAll || (q ? true : openId === s.id);
    return (
      <div key={s.id} data-testid={`guide-section-${s.id}`} className="rounded-2xl border overflow-hidden transition-shadow" style={{ borderColor: "var(--sj-border)", background: "#fff" }}>
        <button
          data-testid={`guide-toggle-${s.id}`}
          onClick={() => setOpenId(openId === s.id ? null : s.id)}
          className="w-full flex items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-[#EDF2F4]"
        >
          <div className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#17A88A12" }}>
            <s.icon className="h-5 w-5" style={{ color: "var(--sj-secondary)" }} />
          </div>
          <span className="flex-1 font-head font-bold text-base" style={{ color: "var(--sj-text)" }}>{markMatches(s.title, q)}</span>
          <ChevronDown className={`no-print h-5 w-5 transition-transform ${isOpen ? "rotate-180" : ""}`} style={{ color: "var(--sj-text-3)" }} />
        </button>
        {isOpen && (
          <ol className="px-5 pb-5 pt-1 space-y-2.5 list-none">
            {s.steps.map((step, i) => (
              <li key={i} className="flex gap-3 text-sm" style={{ color: "var(--sj-text-2)" }}>
                <span className="h-5 w-5 rounded-full shrink-0 flex items-center justify-center text-[11px] font-bold text-white" style={{ background: "var(--sj-ochre)" }}>{i + 1}</span>
                <span className="leading-relaxed">{markMatches(step, q)}</span>
              </li>
            ))}
          </ol>
        )}
      </div>
    );
  };

  const catId = (id) => CATEGORIES.find((c) => c.ids.includes(id))?.key || "admin";
  const grouped = CATEGORIES.map((c) => ({ ...c, items: sections.filter((s) => catId(s.id) === c.key) })).filter((c) => c.items.length);

  return (
    <div className="max-w-3xl mx-auto" data-testid="user-guide-page">
      <div className="sj-page-hero flex items-start justify-between gap-4 mb-2">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl flex items-center justify-center shadow-sm" style={{ background: "var(--sj-primary)" }}>
            <BookOpen className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="font-head text-3xl font-extrabold tracking-tight" style={{ color: "var(--sj-text)" }}>User Guide</h1>
            <p className="text-sm" style={{ color: "var(--sj-text-2)" }}>Everything you need to get the most out of your Streams of Joy portal.</p>
          </div>
        </div>
        <button
          data-testid="guide-print-button"
          onClick={handlePrint}
          className="no-print shrink-0 flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:brightness-105"
          style={{ background: "var(--sj-secondary)" }}
        >
          <Printer className="h-4 w-4" /> Print / Save as PDF
        </button>
      </div>

      <div className="no-print mt-5 relative">
        <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2" style={{ color: "var(--sj-text-3)" }} />
        <input
          data-testid="guide-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search the guide — e.g. attendance, certificate, highlight…"
          className="w-full bg-white border rounded-full pl-11 pr-10 py-3 text-sm outline-none focus:border-[#0B5E4E] shadow-[0_8px_30px_rgba(45,45,45,0.04)]"
          style={{ borderColor: "var(--sj-border)" }}
        />
        {query && (
          <button data-testid="guide-search-clear" onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-[#EDF2F4]" aria-label="Clear search">
            <X className="h-4 w-4" style={{ color: "var(--sj-text-3)" }} />
          </button>
        )}
      </div>

      {!q && (
        <div className="mt-6">
          <QuickStart user={user} />
        </div>
      )}

      <div className="space-y-6">
        {q && sections.length === 0 && (
          <div data-testid="guide-search-empty" className="rounded-2xl border p-8 text-center text-sm" style={{ borderColor: "var(--sj-border)", background: "#fff", color: "var(--sj-text-3)" }}>
            No topics match "{query}". Try a different word, or clear the search.
          </div>
        )}
        {grouped.map((c) => (
          <div key={c.key} data-testid={`guide-category-${c.key}`}>
            <h2 className="font-head text-xs font-bold uppercase tracking-widest mb-2 px-1" style={{ color: "var(--sj-primary)" }}>{c.label}</h2>
            <div className="space-y-3">
              {c.items.map(renderSection)}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 rounded-2xl p-5 flex items-start gap-3" style={{ background: "#0B5E4E0F" }}>
        <Flame className="h-5 w-5 mt-0.5 shrink-0" style={{ color: "var(--sj-primary)" }} />
        <p className="text-sm" style={{ color: "var(--sj-text-2)" }}>
          Need a hand? Speak to a church admin or your team leader and they'll help you find your way around.
        </p>
      </div>
    </div>
  );
}
